import {
  Attribute,
  AttributeLocalizedEnumValue,
  LocalizedString,
  ProductVariant,
  TypedMoney,
} from '@commercetools/platform-sdk';
import {
  INutsVariantAttributes as NutsVariantAttributes,
  pivotAttributeValues,
} from '@nuts/auto-delivery-sdk/dist/utils/helpers';
import { from } from '@nuts/auto-delivery-sdk/dist/utils/money';

import { TagReference } from '@/api/productTagReferences';
import { ShippingCalculatorRequest } from '@/api/shippingCalculator';
import singularize from '@/filters/singularize';
import { DetailProductsByCustomProductKeyProjectionResult } from '@/graphql/customProducts';
import { Product, ProductData, RelatedProduct, Variant } from '@/graphql/productQuery';
import { findAttributeByName } from '@/lib/product/attributes';
import { getListPrice, getMoneyValue, getPriceByChannelKey } from '@/lib/product/prices';
import { Channel } from '@/stores/autoDelivery';
import { GIFT_VARIANTS, isGiftCertificate as isGiftCertificateUtil } from '@/utils/gift';
import { ImageBySize } from '@/utils/image';
import proxiedImageUtil from '@/utils/imageProxied';
import { computeSavings, Money } from '@/utils/money';
import { NutsProductProjection, NutsProductProjectionVariant } from '@/utils/productProjection';

export const MAX_QUANTITY = 4999;

export interface Ancestor {
  id: string;
  name: string;
  url: string;
}

export type VariationKey = 'grindSize' | 'tinColor';

type DiscountedPricesByChannel = Record<
  string,
  | {
      readonly description?: LocalizedString;
      readonly percent: number;
      readonly value: TypedMoney;
    }
  | undefined
>;

export interface NutsProductVariant extends Variant, NutsVariantAttributes {
  readonly attributesRaw: Attribute[];
  readonly countPerPound?: number;
  readonly countryOfOrigin?: AttributeLocalizedEnumValue;
  readonly customProduct?: string;
  readonly images: ImageBySize[];
  readonly ingredients?: string;
  readonly maximumPiecesPerOrder: number;
  readonly merchandisingCategory?: string;
  readonly otherVariations?: {
    key: VariationKey;
    label: string;
    variations: Omit<NutsProductVariant, 'otherVariations'>[];
  }[];
  readonly pieceCost?: TypedMoney;
  readonly productDiscountApplied?: DiscountedPricesByChannel;
  readonly reportingCategory?: string;
  readonly sku: string;
  readonly storageRequirements?: AttributeLocalizedEnumValue;
  readonly titleImage: ImageBySize | null;
  readonly unitName: string | null;
  readonly urlName: string | null;
  readonly variantLabel?: string;
}

export interface NutsProduct extends Pick<ProductData, 'metaDescription' | 'name'> {
  readonly ancestors: Ancestor[];
  readonly contentList?: string;
  readonly customPageTitle?: LocalizedString;
  readonly descriptionHtml: string;
  readonly healthTipsHtml?: string;
  readonly hiddenFromSearch?: boolean;
  readonly images: ImageBySize[];
  readonly key: string;
  readonly masterVariantId: number;
  readonly oneLinerDescription?: string;
  readonly offerGiftAddOns?: boolean;
  readonly productDiscountApplied: boolean;
  readonly testDescriptionHtml: string;
  readonly titleImage: ImageBySize | null;
  readonly upsellingProducts?: RelatedProduct[];
  readonly variantGroups: NutsProductVariant[];
  readonly variants: NutsProductVariant[];
}

export interface PurchaseOptions {
  autoDelivery?: Channel;
  markedAsGift: boolean;
  quantity: number;
  sku: string;
  variation: number;
}

export interface PredictedShipping {
  date: string;
  expiresAt: number;
  expiresInSeconds?: number;
  friendly?: 'today' | 'tomorrow';
}

/**
 * Duplicated from `@nuts/auto-delivery-sdk/dist/utils/helpers`
 */
export type RawAttributeValues<T> = {
  [K in keyof T]-?: {
    name: K;
    value: T[K];
  };
}[keyof T][];

interface Category {
  id: string;
  name: string;
  custom?: {
    customFieldsRaw: { name: string; value: string }[];
  };
}

const getAncestors = (
  categories: (Category & { ancestors: Category[] })[],
  primaryCategoryId?: string,
): Ancestor[] => {
  const primaryCategory = categories.find((c) => c.id === primaryCategoryId);
  if (!primaryCategory) return [];

  const { id, name, ancestors, custom } = primaryCategory;
  let url = '/';
  return [...ancestors, { id, name, custom }]
    .filter((ancestor) => ancestor.name !== 'Nuts.com Categories')
    .map((ancestor) => {
      const categoryUrl = ancestor.custom?.customFieldsRaw?.find(
        (field) => field.name === 'urlName',
      )?.value;
      if (categoryUrl) url += `${categoryUrl}/`;
      return { id: ancestor.id, name: ancestor.name, url };
    });
};

export const getPrice = (
  variant: NutsProductVariant | NutsProductProjectionVariant,
  distributionChannelKey?: string,
) => {
  const { prices } = variant;

  if (!distributionChannelKey || variant.wholesale || !variant.autoDeliveryEligible)
    return getListPrice(prices);

  return getPriceByChannelKey(prices, distributionChannelKey);
};

export const groupBy = <T, K>(list: T[], keyGetter: (value: T) => K) => {
  const map = new Map<K, T[]>();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
};

export const groupVariants = (variants: NutsProductVariant[]): NutsProductVariant[] => {
  const variantGroups: NutsProductVariant[] = [];
  const groupedByWeight = groupBy(variants, (variant) => variant.weight);

  groupedByWeight.forEach((list) => {
    const grindSizeVariation: Omit<NutsProductVariant, 'otherVariations'>[] = [];
    const tinColorVariation: Omit<NutsProductVariant, 'otherVariations'>[] = [];
    const otherVariations: NutsProductVariant['otherVariations'] = [];

    const groupedByGrindSize = groupBy(list, ({ grindSize }) => grindSize?.key);
    groupedByGrindSize.forEach((gridSizeItems, key) => {
      if (key && key !== 'undefined') {
        grindSizeVariation.push(gridSizeItems[0]);
      }
    });
    if (grindSizeVariation.length > 0) {
      otherVariations.push({
        key: 'grindSize',
        label: 'Grind Size',
        variations: grindSizeVariation,
      });
    }

    const groupedByTinColor = groupBy(list, ({ tinColor }) => tinColor?.key);
    groupedByTinColor.forEach((tinColorItems, key) => {
      if (key && key !== 'undefined') {
        tinColorVariation.push(tinColorItems[0]);
      }
    });
    if (tinColorVariation.length > 0) {
      otherVariations.push({
        key: 'tinColor',
        label: 'Tin Color',
        variations: tinColorVariation,
      });
    }

    variantGroups.push({
      ...list[0],
      backordered: list.every(({ backordered }) => backordered),
      otherVariations,
    });
  });

  return variantGroups;
};

/**
 * Gets the unit name shortened and singularized.
 */
export const getUnitName = (variantName?: LocalizedString | string, locale = 'en') => {
  if (!variantName) return null;
  let replacementCount = 0;

  let name = (typeof variantName === 'string' ? variantName : variantName[locale])
    .replace(/ ?\([^(]+\)$/, '')
    .toLocaleLowerCase()
    // first try "cases of 24 cans" => "case of 24 cans"
    .replace(/^(jars|boxes|cases|containers|packs|packages|tins)(?= of )/, (match) => {
      replacementCount += 1;
      return singularize(match);
    });

  if (
    // if we didn't find an initial/outer unit, and
    !replacementCount &&
    // if doesn't look like "12 x 3 ounce bags" (which would work as singular or plural),
    !/^\d+ *x /.test(name)
  ) {
    // ...then try to singularize a trailing noun
    name = name.replace(
      /(?:^| )(bags|blocks|boxes|cases|containers|jars|mixes|packages|packs|pies|samples|tins|trays|bars)$/,
      (match) => singularize(match),
    );
  }

  // finally, always abbreviate "ounce" and "pound"
  return name.replace(' ounce', 'oz').replace(' pound', 'lb');
};

/**
 * Convert our "classic" nnnn-nn SKUs to their "legacy" zero-prefixed form while
 * leaving other SKUs alone.
 *
 * In particular, "PPA-style" SKUs of the form nnnn-nnnL will not be prefixed.
 * Also, if we ever list and sell Kopper's SKUs directly, they will not be
 * prefixed either, as they're not nnnn-nn.
 *
 * This prefixing is only for certain old analytics integrations, i.e. DY and
 * dataLayer/GTM, where changing the string representing an existing SKU could
 * be disruptive by e.g. losing the relationship with historical records and
 * appearing to be a new SKU with no history. Since "PPA-style" skus are
 * naturally new skus, they don't have history and there's no need to prefix
 * them. Over time, we might be able to phase out all "classic" skus. 🤞
 */
export const toLegacySku = (sku: string) => {
  if (/^\d{4}-\d{2}$/.test(sku)) {
    return `0${sku}`;
  }
  return sku;
};

export const NutsProduct = {
  fromCT: (product: Product, customAvailability = 'PDP'): NutsProduct => {
    const {
      key,
      masterData: {
        current: {
          contentList,
          description,
          healthTips,
          masterVariant,
          metaDescription,
          testDescription,
          variants,
        },
      },
    } = product;

    const variantList = [masterVariant, ...variants]
      .map((v): NutsProductVariant => {
        const attributes = pivotAttributeValues(v.attributesRaw).variant;
        const containsProductDiscounts = v.prices.some((p) => p.discounted);
        const variantImages = proxiedImageUtil.getVariants(v.images) ?? [];
        return {
          ...v,
          countPerPound: findAttributeByName(v.attributesRaw, 'countPerPound'),
          countryOfOrigin: findAttributeByName(v.attributesRaw, 'countryOfOrigin'),
          customProduct: findAttributeByName(v.attributesRaw, 'customProduct')?.key,
          images: variantImages,
          ingredients: findAttributeByName(v.attributesRaw, 'ingredients')?.en,
          maximumPiecesPerOrder:
            findAttributeByName(v.attributesRaw, 'maximumPiecesPerOrder') ?? MAX_QUANTITY,
          merchandisingCategory: findAttributeByName(v.attributesRaw, 'merchandisingCategory')?.key,
          otherVariations: [],
          pieceCost: findAttributeByName(v.attributesRaw, 'pieceCost') as TypedMoney | undefined,
          productDiscountApplied: containsProductDiscounts
            ? v.prices.reduce<DiscountedPricesByChannel>((discountsByChannel, price) => {
                if (!price.discounted) return discountsByChannel;
                const listPrice = getListPrice(v.prices);
                const { description: discountDescription } = price.discounted.discount;
                const productDiscountApplied = {
                  ...computeSavings(listPrice.value, price.discounted.value),
                  description: discountDescription ? { en: discountDescription } : undefined,
                };
                return {
                  ...discountsByChannel,
                  [price.channel?.key ?? 'default']: productDiscountApplied,
                };
              }, {})
            : undefined,
          reportingCategory: findAttributeByName(v.attributesRaw, 'reportingCategory')?.key,
          sku: v.sku ?? '',
          storageRequirements: findAttributeByName(v.attributesRaw, 'storageRequirements'),
          titleImage: variantImages[0] ?? null,
          unitName: getUnitName(attributes.variantName),
          urlName: findAttributeByName(v.attributesRaw, 'urlName') ?? null,
          variantLabel: findAttributeByName(v.attributesRaw, 'variantLabel')?.key,
          ...attributes,
        };
      })
      .filter((v) => v.active && v.customAvailability?.includes(customAvailability));

    const variantGroups = groupVariants(variantList);
    const isGiftCertificate = isGiftCertificateUtil(variantGroups);
    const giftVariants: NutsProductVariant[] = GIFT_VARIANTS.map((item) => ({
      ...variantGroups[0],
      prices: [{ id: `${item.variation}`, value: from(item.amount) }],
      id: item.variation,
    }));

    const productImages = proxiedImageUtil.getVariants(masterVariant.images) ?? [];
    const primaryCategoryId = findAttributeByName(
      product.masterData.current.masterVariant.attributesRaw,
      'primaryCategory',
    )?.id;

    return {
      ancestors: getAncestors(product.masterData.current.categories ?? [], primaryCategoryId),
      contentList: contentList ?? '',
      customPageTitle: findAttributeByName(masterVariant.attributesRaw, 'customPageTitle'),
      descriptionHtml: description ?? '',
      healthTipsHtml: healthTips,
      hiddenFromSearch:
        findAttributeByName(masterVariant.attributesRaw, 'hiddenFromSearch') ?? false,
      images: productImages,
      key: key ?? '',
      masterVariantId: masterVariant.id,
      metaDescription,
      name: product.masterData.current.name,
      testDescriptionHtml: testDescription ?? '',
      offerGiftAddOns: findAttributeByName(masterVariant.attributesRaw, 'offerGiftAddOns'),
      oneLinerDescription: findAttributeByName(masterVariant.attributesRaw, 'oneLinerDescription')
        ?.en,
      productDiscountApplied: (isGiftCertificate ? giftVariants : variantList).some(
        (v) => !!v.productDiscountApplied,
      ),
      titleImage: productImages[0] ?? null,
      upsellingProducts: masterVariant.attributesRawRelatedProducts?.find(
        (a) => a.name === 'upsellingProducts',
      )?.referencedResourceSet,
      variantGroups: isGiftCertificate ? giftVariants : variantGroups,
      variants: isGiftCertificate ? giftVariants : variantList,
    };
  },
  fromNutsProductProjection: (
    product: DetailProductsByCustomProductKeyProjectionResult,
  ): NutsProduct => {
    const nutsProductProjection = NutsProductProjection.fromQueryResult(product);

    const [masterVariant] = product.allVariants;
    const productImages = proxiedImageUtil.getVariants(masterVariant.images);
    const { primaryCategory } = nutsProductProjection;

    const variants = product.allVariants.map((variant): NutsProductVariant => {
      const nutsProductProjectionVariant = nutsProductProjection.variants.find(
        (v) => v.id === variant.id,
      );

      if (!nutsProductProjectionVariant) {
        throw new Error(`NutsProductProjectionVariant not found for variant ${variant.id}`);
      }

      return {
        ...nutsProductProjectionVariant,
        attributesRaw: variant.attributesRaw,
        images: proxiedImageUtil.getVariants(variant.images) ?? [],
        maximumPiecesPerOrder: MAX_QUANTITY,
        // Force optional `Money` to optional `TypedMoney`
        pieceCost: nutsProductProjectionVariant.pieceCost as TypedMoney | undefined,
        titleImage: nutsProductProjectionVariant.listImage,
        unitName: getUnitName(nutsProductProjectionVariant.variantName),
        urlName: findAttributeByName(variant.attributesRaw, 'urlName') ?? null,
        // TODO figure out these 2 currently required properties (skipping for Custom Product use case)
        autoBackorder: false,
        snackPack: false,
      };
    });

    return {
      ...product,
      ancestors: getAncestors(product.categories, primaryCategory.id),
      descriptionHtml: product.description,
      images: productImages,
      masterVariantId: masterVariant.id,
      // Purposely skip for Custom Products; must port behavior if we consolidate normal PDP behavior
      productDiscountApplied: false,
      testDescriptionHtml: product.description,
      titleImage: productImages[0] ?? null,
      variantGroups: variants,
      variants,
    };
  },
};

export const isCustomizableProduct = ({ variantGroups }: NutsProduct) => {
  const variant = variantGroups[0];
  return Boolean(variant && 'otherVariations' in variant && variant.otherVariations?.length);
};

export const isGlutenFree = (productTagReferences?: TagReference[]) =>
  !!productTagReferences?.find(({ name }) => name.includes('gluten-free'));

export const isPhysical = (variant?: NutsProductVariant | NutsProductProjectionVariant) =>
  variant ? variant.weight > 0 : false;

export const isAvailableInMixes = (variant: ProductVariant) =>
  findAttributeByName(variant.attributes, 'inMix') === true;

export const isAvailableInTrays = (variant: ProductVariant) =>
  findAttributeByName(variant.attributes, 'inTray') === true;

export const isInStock = (variant: ProductVariant) =>
  findAttributeByName(variant.attributes, 'backordered')?.key === 'no';

export const isBYOB = (variant: ProductVariant) =>
  findAttributeByName(variant.attributes, 'customAvailability')?.includes('BYOB');

/**
 * Check if a variant is a sample or single-serve product
 */
export const isSampleOrSingleServe = <T extends NutsProductVariant | NutsProductProjectionVariant>(
  variant: T,
): boolean => {
  if (variant.sample) {
    return true;
  }
  const unitName = 'unitName' in variant ? variant.unitName : getUnitName(variant.variantName);
  return unitName?.toLowerCase()?.includes('single serve') ?? false;
};

/**
 * Find the smallest eligible variant (non-sample, non-single-serve, with weight)
 *
 * This function presumes that `customAvailability` has already been filtered to only include variants
 * that are available on the PDP.
 */
export const findSmallestEligibleVariant = <
  T extends NutsProductVariant | NutsProductProjectionVariant,
>(
  variants?: T[],
): T | undefined => {
  if (!variants) return undefined;

  const candidates = variants.filter((v) => isPhysical(v) && !isSampleOrSingleServe(v));

  if (!candidates.length) return undefined;

  return candidates.reduce((smallest, current) =>
    current.weight < smallest.weight ? current : smallest,
  );
};

export const priceForQuantity = (
  variant: NutsProductVariant | NutsProductProjectionVariant,
  selections: { quantity: number; autoDelivery?: { key: string } },
  perOunce = false,
) => {
  const price = getPrice(variant, selections.autoDelivery?.key);
  const moneyValue = getMoneyValue(price, selections.quantity);

  return perOunce ? Money.divide(Money.multiply(moneyValue, 1 / variant.weight), 16) : moneyValue;
};

export const isIndividuallyWrapped = (
  variant: Pick<NutsProductVariant, 'bulk' | 'variantName' | 'wholesale'>,
) => {
  if (!(variant.bulk && variant.wholesale)) return true;
  return variant.variantName?.en?.toLowerCase().includes('individually wrapped') || false;
};

export const buildPostalCodeShippingCalculatorRequest = (
  items: { quantity: number; sku: string; weight: number }[],
  address: { country?: string; postalCode: string },
  totalPrice: TypedMoney,
  {
    amazonShippingAllowed = true,
    regionalCarriersAllowed = true,
    dashlinkAllowed = true,
    lasershipAllowed = true,
    ontracAllowed = true,
  } = {},
): ShippingCalculatorRequest => ({
  shipments: [
    {
      address: {
        country: address.country ?? 'US',
        residential: true,
        postalCode: address.postalCode,
      },
      key: 'Address 1',
      lines: items,
      shipmentValue: totalPrice,
    },
  ],
  allowAmazonShipping: Boolean(amazonShippingAllowed),
  allowBlueStreak: Boolean(regionalCarriersAllowed),
  allowCdl: Boolean(regionalCarriersAllowed),
  allowDashlink: Boolean(dashlinkAllowed),
  allowGrandHusky: Boolean(regionalCarriersAllowed),
  allowLasership: Boolean(lasershipAllowed),
  allowOntrac: Boolean(ontracAllowed),
  allowShipx: Boolean(regionalCarriersAllowed),
  allowTforce: Boolean(regionalCarriersAllowed),
  allowUds: Boolean(regionalCarriersAllowed),
});
